/**
 * Sectiemenu — sinds menu-ronde 2 (25 sep 2026) géén eigen kolom meer; sinds ronde 4
 * alleen nog voor secties met `submenu: true` (zie config/navigation.js). De items van
 * de actieve sectie staan ingesprongen onder het actieve hoofditem in de zijbalk
 * (SidebarNav, accordeon). Deze module levert alleen nog de inhoud daarvan:
 * welke schermen, welke actief, en de "Alle …"-link naar de hub.
 *
 * In een account-workspace (/accounts/:id/workspace) zijn dat de tabbladen van dat
 * account (Overzicht, Klantprofiel, …) met "Alle accounts" terug naar de lijst.
 */
import { useLocation, useSearchParams } from "react-router-dom";
import { SECONDARY_NAV_SECTIONS, accordeonItems, sectieItemToegestaan } from "../config/navigation";
import { useUserRole } from "../contexts/UserRoleContext";
import { useVisibilityFilters } from "../contexts/VisibilityFiltersContext";

const opPad = (pathname, pad) => pathname === pad || pathname.startsWith(`${pad}/`);

// [key, label, querystring, vereist recht] — gelijk aan de tabbladkaarten op de
// accountpagina: Cijfers alleen met sales-rechten (de API eist dat ook), de rest voor iedereen.
const WORKSPACE_TABS = [
  ["overview", "Overzicht", ""],
  ["cijfers", "Cijfers", "?tab=cijfers", "sales_dashboard"],
  ["relatie", "Relatiebeheer", "?tab=relatie"],
  ["profiel", "Klantprofiel", "?tab=profiel"],
  ["huisstijl", "Huisstijl", "?tab=huisstijl"],
  ["databeleid", "Databeleid", "?tab=databeleid"],
  ["richtprijzen", "Richtprijzen", "?tab=richtprijzen"],
  ["playbook", "AI Playbook", "?tab=playbook"],
];

/**
 * Inhoud van de uitgeklapte sectie onder een hoofditem.
 * @returns {{ items: Array<{path,label,planned,active}>, alle: {path,label,active}|null } | null}
 */
export function useSectieMenu(sectionKey) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { permissions } = useUserRole();
  const visibility = useVisibilityFilters();
  const pathname = location.pathname;
  const sectie = SECONDARY_NAV_SECTIONS[sectionKey];
  // Ronde 4: alleen secties met `submenu` klappen open (Mijn werk, Accounts). Planning
  // heeft tabs; Productie/AI/Operations starten op hun hub die alles al toont.
  if (!sectie || !sectie.submenu) return null;

  // Account-workspace: tabbladen van dit account
  const ws = sectionKey === "accounts" ? pathname.match(/^\/accounts\/([^/]+)\/workspace$/) : null;
  if (ws) {
    const tab = searchParams.get("tab") || "overview";
    return {
      items: WORKSPACE_TABS.filter(([, , , recht]) => !recht || permissions?.[recht]?.view).map(([key, label, qs]) => ({
        path: `/accounts/${ws[1]}/workspace${qs}`,
        label,
        active: tab === key,
      })),
      alle: { path: sectie.hubPath, label: sectie.alleLabel || "Alle accounts", active: false },
    };
  }

  const toegestaan = sectie.items.filter((i) => sectieItemToegestaan(i, permissions, visibility));
  const isActief = (i) => opPad(pathname, i.path);
  const start = sectie.start || [];
  // Het startscherm (Mijn werk → Vandaag) staat er altijd bij en telt niet mee voor de vier
  const { items, verborgen } = accordeonItems(toegestaan, sectie.hubPath, isActief, [...start, ...(sectie.vast || [])]);
  const startIsHub = start.length === 0 || start.includes(sectie.hubPath);
  const toonAlle = verborgen > 0 || !startIsHub;
  return {
    items: items.map((i) => ({ path: i.path, label: i.label, planned: !!i.planned, active: isActief(i) })),
    alle: toonAlle
      ? { path: sectie.hubPath, label: sectie.alleLabel || `Alle ${sectie.label.toLowerCase()}`, active: pathname === sectie.hubPath }
      : null,
  };
}
